// frontend/src/utils/frame/frameEvaluator.js

import {
  FRAME_RULES,
  FRAME_PRIORITY,
  normalizeText,
  getDefaultFrame,
} from './frameRules';

function findMatchedTriggers(message = '', triggers = []) {
  const lower = normalizeText(message);
  return triggers.filter((trigger) => lower.includes(normalizeText(trigger)));
}

function buildFrameFromRule(rule, matchedTriggers = [], lastUpdatedBy = 'inferred') {
  const base = rule.confidence;

  // 🔥 NEW: scale confidence based on trigger strength
  const triggerBoost = Math.min(matchedTriggers.length * 0.1, 0.4);

  const dynamicConfidence = Math.min(base + triggerBoost, 0.95);

  return {
    type: rule.type,
    confidence: dynamicConfidence,
    locked: matchedTriggers.some((trigger) => rule.hardLockTriggers.includes(trigger)),
    lastUpdatedBy,
    allowedBehaviors: [...rule.allowedBehaviors],
    blockedBehaviors: [...rule.blockedBehaviors],
    matchedTriggers,
  };
}

function isFreshOpenMessage(message = '') {
  const normalized = normalizeText(message).replace(/[.,!?]+$/g, '');

  const freshOpeners = new Set([
    'hi',
    'hello',
    'hey',
    'hiya',
    'yo',
    'hello daisy',
    'hi daisy',
    'hey daisy',
    'good morning',
    'good afternoon',
    'good evening',
    'morning',
    'afternoon',
    'evening',
  ]);

  return freshOpeners.has(normalized);
}

function evaluateFrame({
  message = '',
  currentFrame = null,
} = {}) {
  const lower = normalizeText(message);
  const defaultFrame = getDefaultFrame();
  const fallbackFrame = currentFrame || defaultFrame;
  const isFreshOpen = isFreshOpenMessage(message);

  if (!lower) {
    return fallbackFrame;
  }

  if (isFreshOpen) {
    return {
      ...defaultFrame,
      lastUpdatedBy: 'fresh_open_reset',
      matchedTriggers: [],
      previousFrameType: currentFrame?.type || null,
    };
  }

  if (currentFrame?.locked) {
    const currentRule = FRAME_RULES[currentFrame.type];
    const currentMatches = currentRule
      ? findMatchedTriggers(lower, currentRule.triggers)
      : [];

    if (currentMatches.length > 0) {
      return {
        ...currentFrame,
        confidence: Math.max(currentFrame.confidence || 0, 0.95),
        matchedTriggers: currentMatches,
      };
    }

    return currentFrame;
  }

  for (const frameType of FRAME_PRIORITY) {
    const rule = FRAME_RULES[frameType];
    const matchedTriggers = findMatchedTriggers(lower, rule.triggers);

    if (matchedTriggers.length > 0) {
      return buildFrameFromRule(rule, matchedTriggers, 'user_inferred');
    }
  }

return {
  ...fallbackFrame,
  confidence: Math.max(fallbackFrame.confidence || 0.3, 0.4), // slight lift from idle
  lastUpdatedBy: 'carry_forward',
};
}

export { evaluateFrame };